import { useRef, useState, useEffect, useMemo } from 'react';
import { useIntl } from 'umi';
import type { UseKPIOverviewProps } from '@intelligence/pages/TopCharts/PCGames/_config/type';
import {
  PC_CONSOLE_COMBINE_ENTITY_TYPE,
  MONTHLY,
  DAILY,
} from '@intelligence/pages/TopCharts/_config/topChartsConst';
import { isEmpty } from '@/pages/intelligence/utils/commonUtils';
import {
  getDetailIndexRankCombine,
  fetchEngagementData,
  fetchSteamCardData,
  fetchStreamingCardData,
  fetchUSRevenueUnits,
  fetchDatabrainRevenueUnits,
  getGameBasicinfoCombine,
  steamKpiPlaytime,
  steamCountiesPlayer,
} from '@intelligence/services/topcharts';
import { commonKPIOverviewParams } from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/utils';
import {
  getFormattedCardData,
  indicatorTitleContent,
  // getSegmentUSRevenueUnitsTitle,
} from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overview/PerformanceSummary/cardUtils';
import { mergeCardRankData, transRankParams } from '@/pages/intelligence/utils/topchartsUtils';
import { toDateTypeNor } from '@intelligence/utils/common';
import { COMBINEID_REG } from '@intelligence/utils/constant';
// import { FileWordFilled } from '@ant-design/icons';

const CANCELED = 'canceled';

// KPIOverview获取数据与接口交互的hooks
const useKPIOverview = (props: UseKPIOverviewProps) => {
  const {
    gameId,
    entityType = PC_CONSOLE_COMBINE_ENTITY_TYPE,
    // steamDateType = '',
    isCN = false,
  } = props || {};
  // console.log('useKPIOverview', props);
  const intl = useIntl();
  const [usRevenueUnitsDatas, setUSRevenueUnitsDatas] = useState<any>([]);
  const [databrainRevenueUnitsDatas, setDatabrainRevenueUnitsDatas] = useState<any>([]);
  const [engagementDataList, setEngagementDataList] = useState<any>([]);
  const [steamDataList, setSteamDataList] = useState<any>([]);
  const [steamHourlyCcuRawItem, setSteamHourlyCcuRawItem] = useState<any>(null);
  const [streamingDataList, setStreamingDataList] = useState<any>([]);
  const [usRevenueUnitsLoading, setUSRevenueUnitsLoading] = useState(true);
  const [databrainRevenueUnitsLoading, setDatabrainRevenueUnitsLoading] = useState(true);
  const [engagementLoading, setEngagementLoading] = useState(true);
  const [steamLoading, setSteamLoading] = useState(true);
  const [streamingLoading, setStreamingLoading] = useState(true);
  const [engagementRankLoading, setEngagementRankLoading] = useState(true);
  const [steamRankLoading, setSteamRankLoading] = useState(true);
  const [streamingRankLoading, setStreamingRankLoading] = useState(true);
  const [kpiPlayerData, setKpiPlayerData] = useState({});
  const [kpiPlaytimeData, setKpiPlaytimeData] = useState([]);
  const [combineBasicInfo, setCombineBasicInfo] = useState<any>(null);
  const [combineBasicInfoLoading, setCombineBasicInfoLoading] = useState<boolean>(false);
  // const [platformList, setPlatformList] = useState([]);

  // 是否支持 Databrain 校准数据（由 combineBasicInfo.support_metrics 推导）
  const hasDatabrainCalibratedData = useMemo(() => {
    const metrics = combineBasicInfo?.support_metrics;
    return Boolean((metrics as any)?.databrain);
  }, [combineBasicInfo]);

  // 用于取消请求的 AbortController Refs
  const revenueUnitAbortControllerRef = useRef<AbortController>();
  const databrainRevenueUnitAbortControllerRef = useRef<AbortController>();
  const steamDataAbortControllerRef = useRef<AbortController>();
  const steamingDataAbortControllerRef = useRef<AbortController>();

  // 自动获取 combine basic info（进入或 gameId 变化时）
  useEffect(() => {
    if (gameId && COMBINEID_REG.test(gameId)) {
      getCombineBasicInfo();
    }
  }, [gameId]);

  // 当语言切换时，重新本地化已存在卡片的标题（无需重新拉取数据）
  useEffect(() => {
    const updateTitles = (list: any[]) =>
      Array.isArray(list)
        ? list.map((item: any) => ({
            ...item,
            titleContent: indicatorTitleContent(
              item?.metricUserGuide,
              item?.data_source,
              item?.conditionPlatform,
              intl,
            ),
          }))
        : list;

    setUSRevenueUnitsDatas((prev: any[]) => updateTitles(prev));
    setDatabrainRevenueUnitsDatas((prev: any[]) => updateTitles(prev));
    setEngagementDataList((prev: any[]) => updateTitles(prev));
    setSteamDataList((prev: any[]) => updateTitles(prev));
    setStreamingDataList((prev: any[]) => updateTitles(prev));
  }, [(intl as any)?.locale]);

  // 获取USRevenueUnits卡片数据
  const getUSRevenueUnits = async (timeType: string) => {
    revenueUnitAbortControllerRef.current?.abort();
    revenueUnitAbortControllerRef.current = new AbortController();
    setUSRevenueUnitsLoading(true);
    const pcconsoleParamObj = {
      id: gameId,
      date_type: timeType,
    };
    setUSRevenueUnitsDatas([]);
    let isCanceled;
    try {
      const USRevenueRes = await fetchUSRevenueUnits(pcconsoleParamObj, {
        signal: revenueUnitAbortControllerRef.current?.signal,
      });
      if (!isEmpty(USRevenueRes)) {
        const detailIndex = USRevenueRes?.data;

        const cardDataList =
          getFormattedCardData(
            detailIndex,
            entityType,
            toDateTypeNor[USRevenueRes?.req?.date_type],
            'US Revenue & Units',
            true,
            isCN,
            timeType,
            intl,
          ) || [];
        setUSRevenueUnitsDatas(cardDataList);
        // setPlatformList();
      }
    } catch (err: any) {
      console.log(err);
      isCanceled = err?.msg === CANCELED;
    } finally {
      !isCanceled && setUSRevenueUnitsLoading(false);
    }
  };

  // 获取Databrain Revenue & Units卡片数据（同USRevenue参数）
  const getDatabrainRevenueUnits = async (timeType: string) => {
    databrainRevenueUnitAbortControllerRef.current?.abort();
    databrainRevenueUnitAbortControllerRef.current = new AbortController();
    setDatabrainRevenueUnitsLoading(true);
    const pcconsoleParamObj = {
      id: gameId,
      date_type: timeType,
    };
    setDatabrainRevenueUnitsDatas([]);
    let isCanceled;
    try {
      const dbRevenueRes = await fetchDatabrainRevenueUnits(pcconsoleParamObj, {
        signal: databrainRevenueUnitAbortControllerRef.current?.signal,
      });
      if (!isEmpty(dbRevenueRes)) {
        const detailIndex = dbRevenueRes?.data;

        console.log('dbRevenueRes', dbRevenueRes);

        const cardDataList =
          getFormattedCardData(
            detailIndex,
            entityType,
            toDateTypeNor[dbRevenueRes?.req?.date_type],
            'Databrain Revenue & Units',
            true,
            isCN,
            timeType,
            intl,
          ) || [];
        setDatabrainRevenueUnitsDatas(cardDataList);
      }
    } catch (err: any) {
      console.log(err);
      isCanceled = err?.msg === CANCELED;
    } finally {
      !isCanceled && setDatabrainRevenueUnitsLoading(false);
    }
  };

  // 获取 PC/Console 聚合基本信息（/combine/basic_info）
  const getCombineBasicInfo = async (
    options: { about?: 0 | 1; similar_game?: 0 | 1 } = { about: 1, similar_game: 0 },
  ) => {
    setCombineBasicInfoLoading(true);
    try {
      const payload = {
        entity_type: PC_CONSOLE_COMBINE_ENTITY_TYPE as 'combine',
        id: gameId,
        about: options?.about ?? 1,
        similar_game: options?.similar_game ?? 0,
      };
      const res = await getGameBasicinfoCombine(payload);
      if (!isEmpty(res)) {
        setCombineBasicInfo(res?.data);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setCombineBasicInfoLoading(false);
    }
  };

  // 获取Engagement卡片数据
  const getEngagementData = async () => {
    setEngagementLoading(true);
    const pcconsoleParamObj = {
      ...commonKPIOverviewParams,
      id: gameId,
      date_type: MONTHLY,
    };
    setEngagementDataList([]);
    // 添加融合ID校验规则
    if (COMBINEID_REG.test(pcconsoleParamObj?.id)) {
      try {
        const engagementRes = await fetchEngagementData(pcconsoleParamObj);
        if (!isEmpty(engagementRes)) {
          const detailIndex = engagementRes?.data;
          const monthList = detailIndex?.filter((item: any) =>
            ['ampere_mau', 'ampere_lifetime_users', 'ampere_avg_monthly_playtime']?.includes(
              item?.metric,
            ),
          );
          const dayList = detailIndex?.filter((item: any) =>
            ['ampere_dau', 'ampere_d1', 'ampere_d7']?.includes(item?.metric),
          );
          const cardDataListMonth =
            getFormattedCardData(monthList, entityType, 'month', '', true, isCN, '', intl) || [];
          const cardDataListDay = getFormattedCardData(dayList, entityType, 'day', '', true, isCN, '', intl) || [];
          setEngagementDataList([...cardDataListMonth, ...cardDataListDay]);

          const rank_date_map = transRankParams(detailIndex, engagementRes?.req?.date_type);

          const rankApiData = {
            ...pcconsoleParamObj,
            rank_date_map,
          };
          // 获取Ranking数据
          getPCConsoleRankData(
            detailIndex,
            rankApiData,
            setEngagementDataList,
            setEngagementRankLoading,
            true,
          );
        }
      } catch (err) {
        console.log(err);
      } finally {
        setEngagementLoading(false);
      }
    } else {
      setEngagementLoading(false);
    }
  };

  // 获取SteamData卡片数据
  const getSteamCardData = async (dateType: string = DAILY) => {
    steamDataAbortControllerRef.current?.abort();
    steamDataAbortControllerRef.current = new AbortController();
    setSteamLoading(true);
    const pcconsoleParamObj = {
      ...commonKPIOverviewParams,
      id: gameId,
      date_type: dateType,
    };
    const defaultSteamParams = {
      entity_type: 'combine',
      id: gameId,
      scale: 1,
    };
    setSteamDataList([]);
    setSteamHourlyCcuRawItem(null);

    let isCanceled;
    // 添加游戏ID校验规则
    if (COMBINEID_REG.test(gameId)) {
      try {
        const steamDataRes = await fetchSteamCardData(pcconsoleParamObj, {
          signal: steamDataAbortControllerRef.current?.signal,
        });
        const kpiPlaytime = await steamKpiPlaytime(defaultSteamParams);
        const playersByCountry = await steamCountiesPlayer({ ...defaultSteamParams, count: 7 });
        setKpiPlayerData(kpiPlaytime);
        setKpiPlaytimeData(playersByCountry);
        if (!isEmpty(steamDataRes)) {
          const detailIndex = steamDataRes?.data;
          const hourlyCcuRawItem = detailIndex?.find(
            (item: any) => item?.metric === 'source_steam__hourly_ccu',
          );
          setSteamHourlyCcuRawItem(hourlyCcuRawItem ?? null);
          const cardDataList =
            getFormattedCardData(detailIndex, entityType, toDateTypeNor[dateType], '', true, isCN, '', intl) || [];
          setSteamDataList(cardDataList);

          const rank_date_map = transRankParams(detailIndex, steamDataRes?.req?.date_type);

          const rankApiData = {
            ...pcconsoleParamObj,
            rank_date_map,
          };
          // 获取Ranking数据
          getPCConsoleRankData(detailIndex, rankApiData, setSteamDataList, setSteamRankLoading);
        }
      } catch (err: any) {
        isCanceled = err?.msg === CANCELED;
      } finally {
        !isCanceled && setSteamLoading(false);
      }
    } else {
      !isCanceled && setSteamLoading(false);
    }
  };

  // 获取Streaming卡片数据
  const getStreamingCardData = async (dateType: string = MONTHLY) => {
    setStreamingLoading(true);
    steamingDataAbortControllerRef.current?.abort();
    steamingDataAbortControllerRef.current = new AbortController();
    setTimeout(() => setStreamingLoading(true));
    const pcconsoleParamObj = {
      ...commonKPIOverviewParams,
      id: gameId,
      date_type: dateType,
    };

    setStreamingDataList([]);
    let isCanceled;
    // 添加游戏ID校验规则
    if (COMBINEID_REG.test(gameId)) {
      try {
        const steamingRes = await fetchStreamingCardData(pcconsoleParamObj, {
          signal: steamingDataAbortControllerRef.current?.signal,
        });

        if (!isEmpty(steamingRes)) {
          setStreamingDataList(steamingRes?.data);
          const detailIndex = steamingRes?.data;
          const cardDataList =
            getFormattedCardData(detailIndex, entityType, toDateTypeNor[dateType], '', true, isCN, '', intl) || [];
          setStreamingDataList(cardDataList);

          const rank_date_map = transRankParams(detailIndex, steamingRes?.req?.date_type);

          const rankApiData = {
            ...pcconsoleParamObj,
            rank_date_map,
          };
          // 获取Ranking数据
          getPCConsoleRankData(
            detailIndex,
            rankApiData,
            setStreamingDataList,
            setStreamingRankLoading,
          );
        }
      } catch (err: any) {
        console.log(err);
        isCanceled = err?.msg === CANCELED;
      } finally {
        !isCanceled && setStreamingLoading(false);
      }
    } else {
      !isCanceled && setStreamingLoading(false);
    }
  };

  // 获取Rank数据
  const getPCConsoleRankData = async (
    list: any[],
    params: any,
    setDetailIndexDataList: Function,
    rankLoading: Function,
    isNewAmpere = false,
  ) => {
    // 做接口参数兜底，防止报错
    const regions = params?.regions;
    const ranks = params?.rank_date_map;
    // 校验入参中regions不为空
    if (!Array.isArray(regions) || !regions?.length) return;
    // 校验入参中rank_date_map不为空
    if (!Array.isArray(ranks) || !ranks?.length) return;
    rankLoading?.(true);
    try {
      const paramsTemp = {
        ...params,
      };
      const resRank = await getDetailIndexRankCombine(paramsTemp);
      const newList = mergeCardRankData(list, resRank?.data);
      if (isNewAmpere) {
        const monthList = [...newList]?.filter((item: any) =>
          ['ampere_mau', 'ampere_lifetime_users', 'ampere_avg_monthly_playtime']?.includes(
            item?.metric,
          ),
        );
        const dayList = [...newList]?.filter((item: any) =>
          ['ampere_dau', 'ampere_d1', 'ampere_d7']?.includes(item?.metric),
        );
        const cardDataListMonth =
          getFormattedCardData(monthList, entityType, 'month', '', true, isCN, '', intl) || [];
        const cardDataListDay = getFormattedCardData(dayList, entityType, 'day', '', true, isCN, '', intl) || [];
        setDetailIndexDataList([...cardDataListMonth, ...cardDataListDay]);
      } else {
        /* 合并卡片和Rank内容后回填到卡片 */
        const cardDataList =
          getFormattedCardData(
            [...newList],
            entityType,
            toDateTypeNor[resRank?.req?.date_type],
            '',
            true,
            isCN,
            '',
            intl,
          ) || [];

        setDetailIndexDataList(cardDataList);
      }
    } finally {
      rankLoading?.(false);
    }
  };

  return {
    getUSRevenueUnits,
    getDatabrainRevenueUnits,
    getEngagementData,
    getSteamCardData,
    getStreamingCardData,
    engagementLoading,
    steamLoading,
    streamingLoading,
    usRevenueUnitsDatas,
    databrainRevenueUnitsDatas,
    engagementDataList,
    steamDataList,
    steamHourlyCcuRawItem,
    streamingDataList,
    usRevenueUnitsLoading,
    databrainRevenueUnitsLoading,
    engagementRankLoading,
    steamRankLoading,
    streamingRankLoading,
    kpiPlayerData,
    kpiPlaytimeData,
    combineBasicInfo,
    combineBasicInfoLoading,
    hasDatabrainCalibratedData,
  };
};

export default useKPIOverview;
